import { CircleInfoIcon } from "@xola/icons";
import { Checkbox, flash, Label, Modal, SubmitButton, Switch, Tooltip } from "@xola/ui-kit";
import { clsx } from "clsx";
import {
    FormField,
    InputField,
    isExperienceProduct,
    isPartnerPackageProduct,
    nullableString,
    RadioInput,
    requiredString,
} from "common";
import { FormikHelpers } from "formik";
import { uniqBy } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation } from "react-query";
import {
    ALL_PRODUCT_OBJECTS,
    Button,
    ButtonType,
    CollectionNode,
    CollectionNodeObject,
    Merchandise,
    MerchandiseSource,
    PartnerPackage,
    Permission,
    Product,
    ProductObject,
    productTitleMap,
} from "types";
import { boolean, object } from "yup";
import { RemoveProductTypesModal } from "../RemoveProductTypesModal";
import { DragDropComponent } from "./DragAndDropComponent";
import { api } from "@/api/api";
import { ProductObjectFilterDropdown, type ProductObjectFilter } from "@/components/Common/ProductObjectFilter";
import { SearchInput } from "@/components/Common/SearchInput";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { Loader } from "@/components/Loader";
import { MultiSelectPopover } from "@/components/MultiSelectPopover";
import { allowCollectionsInMixedButtons } from "@/flag";
import { usePartnerExperiences } from "@/hooks/resources/useProduct";
import { useSeller } from "@/hooks/useAccount";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";
import {
    filterInsertListingFromButton,
    getActiveButtonsByType,
    getButtonObjectType,
    getInitialValue,
    hasPartnerProducts,
} from "@/models/ButtonCode";
import { isExperiencePublished } from "@/models/Experience";
import { MERCHANDISE_API_PATH } from "@/models/Merchandise";
import { hasX2CheckoutWithFullProbability } from "@/models/Seller";
import { getCollection } from "@/utils/getCollection";

interface MultipleItemCheckoutModalProps {
    isOpen: boolean;
    onClose: () => void;
    selectedEditButton: Button | undefined;
    buttonType: ButtonType;
}

export const MultipleItemCheckoutModal = ({
    onClose,
    isOpen,
    selectedEditButton,
    buttonType,
}: MultipleItemCheckoutModalProps) => (
    <Modal size="huge" isOpen={isOpen} onClose={onClose}>
        <ModalContent selectedEditButton={selectedEditButton} buttonType={buttonType} onClose={onClose} />
    </Modal>
);

interface ModalContentProps {
    onClose: () => void;
    selectedEditButton: Button | undefined;
    buttonType: ButtonType;
}

interface InitialValues {
    buttonName: string;
    products: Product[];
    displayedProducts: Product[];
    autoApplyCode?: string | null;
    isAutoApplyCode: boolean;
}

const ModalContent = ({ onClose, selectedEditButton, buttonType }: ModalContentProps) => {
    const invalidate = useInvalidate();
    const [selectedButtons, setSelectedButtons] = useState<string[]>([]);
    const [selectedButtonType, setSelectedButtonType] = useState(buttonType);
    const [productObjectFilters, setProductObjectFilters] = useState<ProductObjectFilter[]>([]);
    const [pendingProductObjectFilters, setPendingProductObjectFilters] = useState<ProductObjectFilter[] | null>(null);
    const [pendingRemovedProductTypes, setPendingRemovedProductTypes] = useState<
        { productObject: ProductObject; count: number }[]
    >([]);

    const seller = useSeller();
    const { id: sellerId } = seller;

    const validationSchema = useMemo(() => {
        return object({
            buttonName: requiredString(),
            isAutoApplyCode: boolean(),
            autoApplyCode: nullableString().test("autoApplyCode-required", "Code is required", (value, context) => {
                const { isAutoApplyCode, displayedProducts } = context.parent;
                return !isAutoApplyCode || hasPartnerProducts(displayedProducts, sellerId) || !!value;
            }),
        });
    }, [sellerId]);

    const [notDisplayedSearch, setNotDisplayedSearch] = useState("");
    const [displayedSearch, setDisplayedSearch] = useState("");
    // SearchInput maintains its own localValue state and doesn't sync when the value prop changes.
    // Bumping this key on Clear All forces SearchProducts to remount, re-initializing localValue from the empty value prop.
    const [searchKey, setSearchKey] = useState(0);

    const { isGranted } = useAuthorization();

    const isGiftButton = selectedButtonType === ButtonType.GIFT;
    const isTimelineButton = selectedButtonType === ButtonType.TIMELINE;
    const isDayView = selectedButtonType === ButtonType.DAY;
    const canViewMemberships = isGranted(Permission.MEMBERSHIPS_VIEW);
    const canViewMerchandise = isGranted(Permission.MERCHANDISE_VIEW);
    const canViewPackages = isGranted(Permission.PACKAGES_VIEW);
    const supportedProductObjects = useMemo(() => {
        if (isGiftButton) {
            return [ProductObject.GIFT_OFFER];
        }

        if (isTimelineButton) {
            return [ProductObject.EXPERIENCE];
        }

        return [
            ProductObject.EXPERIENCE,
            ...(canViewPackages ? [ProductObject.PACKAGE] : []),
            ProductObject.PARTNER_PACKAGE,
            ...(canViewMemberships ? [ProductObject.MEMBERSHIP] : []),
            ProductObject.GIFT_OFFER,
            ...(canViewMerchandise ? [ProductObject.WARE] : []),
            ProductObject.COLLECTION,
        ];
    }, [canViewMemberships, canViewMerchandise, canViewPackages, isGiftButton, isTimelineButton]);

    const { data: productsCollection } = useAutoFetchCollection<Product>("products", null);

    const products = useMemo(() => {
        const source = productsCollection ?? [];
        return source.filter((product) => {
            if (!supportedProductObjects.includes(product.object)) {
                return false;
            }

            // Merchandise (WARE) is handled separately via useAutoFetchCollection with CHECKOUT source filtering.
            // Excluding it here prevents unfiltered WARE items from the products cache from bypassing that filter.
            if (product.object === ProductObject.WARE) {
                return false;
            }

            const isPublished = isExperienceProduct(product) ? isExperiencePublished(product) : true;
            return isPublished;
        });
    }, [productsCollection, supportedProductObjects]);

    const showPartnerProducts = !isGiftButton && !isTimelineButton;
    const { data: partnerPackages, isFetching: isFetchingPartnerPackages } = useAutoFetchCollection<PartnerPackage>(
        "partnerPackages",
        null,
        { enabled: showPartnerProducts },
    );

    const { data: partnerExperiences, isFetching: isFetchingPartnerExperiences } = usePartnerExperiences({
        enabled: showPartnerProducts,
    });
    const { data: collections, isFetching: isFetchingCollections } = useAutoFetchCollection<CollectionNode>(
        "collectionNodes",
        { object: CollectionNodeObject.GROUP_COLLECTION_NODE },
        { enabled: !isGiftButton && !isTimelineButton && !isDayView },
    );
    const { data: merchandises, isFetching: isFetchingMerchandises } = useAutoFetchCollection<Merchandise>(
        MERCHANDISE_API_PATH,
        null,
        { enabled: !isGiftButton && !isTimelineButton && !isDayView && !!canViewMerchandise },
    );
    const partnerProducts = useMemo(() => {
        return showPartnerProducts ? [...partnerExperiences, ...partnerPackages] : [];
    }, [partnerExperiences, partnerPackages, showPartnerProducts]);

    const filteredProducts = useMemo(() => {
        const checkoutMerchandises = (merchandises ?? []).filter((merchandise) =>
            merchandise.allowedSources?.includes(MerchandiseSource.CHECKOUT),
        );
        const merchandiseProducts =
            !isGiftButton && !isTimelineButton && !isDayView && canViewMerchandise ? checkoutMerchandises : [];
        const collectionProducts = !isGiftButton && !isTimelineButton && !isDayView ? collections : [];
        const mappedCollections: (Product & { collection?: CollectionNode })[] = (collectionProducts ?? []).map(
            (collection) => ({
                id: collection.id,
                name: collection.breadcrumb ?? collection.name ?? "Collection",
                object: ProductObject.COLLECTION,
                seller: { id: collection.seller?.id ?? "" },
                collection,
            }),
        );
        const combinedProducts = [
            ...products,
            ...(showPartnerProducts ? partnerProducts : []),
            ...(merchandiseProducts ?? []),
            ...mappedCollections,
        ];
        const uniqueProducts = uniqBy(combinedProducts, (product) => product.id);
        return uniqueProducts.filter((product) => {
            return !isExperienceProduct(product) || isExperiencePublished(product) || isPartnerPackageProduct(product);
        });
    }, [
        collections,
        canViewMerchandise,
        isDayView,
        isTimelineButton,
        merchandises,
        isGiftButton,
        partnerProducts,
        products,
        showPartnerProducts,
    ]);

    // Stable key: same string when the set of product object types is unchanged. Prevents useEffect from resetting filters on every render (which was overwriting checkbox toggles).
    const productObjectTypesKey = useMemo(() => {
        const types = new Set(filteredProducts.map((p) => p.object));
        return [...types].sort().join(",");
    }, [filteredProducts]);

    const displayedProductObjects = useMemo(() => {
        if (!selectedEditButton) {
            return [];
        }

        const objects = new Set<ProductObject>();
        for (const item of selectedEditButton.items) {
            const product = filteredProducts.find((candidate) => candidate.id === item.product.id);
            if (product) {
                objects.add(product.object);
            }
        }

        return [...objects];
    }, [filteredProducts, selectedEditButton]);

    const defaultEnabledProductObjects = useMemo(() => {
        if (isGiftButton) {
            return [ProductObject.GIFT_OFFER];
        }
        if (isTimelineButton) {
            return [ProductObject.EXPERIENCE];
        }

        if (isDayView) {
            // Day view pre-enables all its supported product types: Experiences, Packages,
            // Partner Packages, and Memberships (permission-gated).
            const dayDefaults = new Set<ProductObject>([
                ProductObject.EXPERIENCE,
                ProductObject.PARTNER_PACKAGE,
                ...(canViewPackages ? [ProductObject.PACKAGE] : []),
                ...(canViewMemberships ? [ProductObject.MEMBERSHIP] : []),
                ...displayedProductObjects,
            ]);
            return supportedProductObjects.filter((productObject) => dayDefaults.has(productObject));
        }

        const defaults = new Set<ProductObject>([
            ProductObject.EXPERIENCE,
            ProductObject.PACKAGE,
            ...displayedProductObjects,
        ]);

        const filtered = supportedProductObjects.filter((productObject) => defaults.has(productObject));

        // If no standard defaults match the available products, enable all available types
        // so sellers who only have e.g. merchandise can still see their products.
        if (filtered.length === 0) {
            const availableTypes = new Set(filteredProducts.map((product) => product.object));
            return supportedProductObjects.filter((productObject) => availableTypes.has(productObject));
        }

        return filtered;
    }, [
        canViewMemberships,
        canViewPackages,
        displayedProductObjects,
        filteredProducts,
        isDayView,
        isTimelineButton,
        isGiftButton,
        supportedProductObjects,
    ]);

    const productObjectFiltersKey = useMemo(() => {
        const enabledKey = [...defaultEnabledProductObjects].sort().join(",");
        return `${productObjectTypesKey}|${enabledKey}`;
    }, [defaultEnabledProductObjects, productObjectTypesKey]);

    // Initialize product type filters when the set of product types changes (e.g. modal open, button type change).
    useEffect(() => {
        const productObjectCountMap = new Map<ProductObject, number>();
        for (const product of filteredProducts) {
            const count = productObjectCountMap.get(product.object) ?? 0;
            productObjectCountMap.set(product.object, count + 1);
        }
        const filters: ProductObjectFilter[] = [];
        for (const productObject of ALL_PRODUCT_OBJECTS) {
            const count = productObjectCountMap.get(productObject) ?? 0;
            if (count > 0) {
                filters.push({
                    productObject,
                    label: productTitleMap[productObject],
                    count: 0,
                    enabled: defaultEnabledProductObjects.includes(productObject),
                });
            }
        }
        setProductObjectFilters(filters);
    }, [defaultEnabledProductObjects, filteredProducts, productObjectFiltersKey]);

    const { mutateAsync: createButton, isLoading } = useMutation(async (values: InitialValues) => {
        await api.post(`/buttons`, {
            items: values.displayedProducts.map((product: Product, index: number) => ({
                name: product.name,
                object: getButtonObjectType(product),
                product: { id: product.id },
                sequence: index,
            })),
            name: values.buttonName,
            type: selectedButtonType,
            autoApplyCode: values.isAutoApplyCode ? values.autoApplyCode : null,
            seller: { id: seller.id },
        });
        invalidate("buttons");
        flash.success("Button successfully created");
    });

    const { mutateAsync: updateButton, isLoading: isLoadingUpdating } = useMutation(
        async ({ values, selectedEditButton }: { values: InitialValues; selectedEditButton: Button }) => {
            await api.put(`/buttons/${selectedEditButton.id}`, {
                items: values.displayedProducts.map((product: Product, index: number) => ({
                    name: product.name,
                    object: getButtonObjectType(product),
                    product: { id: product.id },
                    sequence: index,
                })),
                name: values.buttonName,
                type: selectedButtonType,
                autoApplyCode: values.isAutoApplyCode ? values.autoApplyCode : null,
                seller: { id: seller.id },
            });
            invalidate("buttons");
            flash.success("Button successfully updated");
        },
    );

    const handleSubmit = async (values: InitialValues, { setFieldError }: FormikHelpers<InitialValues>) => {
        const submittedValues = hasPartnerProducts(values.displayedProducts, sellerId)
            ? { ...values, isAutoApplyCode: false, autoApplyCode: null }
            : values;

        try {
            await (selectedEditButton
                ? updateButton({ values: submittedValues, selectedEditButton })
                : createButton(submittedValues));
            onClose();
        } catch (error: any) {
            // Check if the API returned field-specific errors
            const errorMessage = error.response?.data?.field?.autoApplyCode?.message;
            if (errorMessage) {
                setFieldError("autoApplyCode", errorMessage);
            } else {
                console.error("Error updating button", error);
                flash.danger("Error updating button");
            }
        }
    };

    const initialValues: InitialValues = {
        buttonName: selectedEditButton?.name ?? "",
        products: getInitialValue(selectedButtons, filteredProducts, selectedEditButton, "notDisplayed"),
        displayedProducts: getInitialValue(selectedButtons, filteredProducts, selectedEditButton, "displayed"),
        autoApplyCode: selectedEditButton?.autoApplyCode ?? null,
        isAutoApplyCode: !!selectedEditButton?.autoApplyCode,
    };

    const handleOnCheckButton = (item: Button) => {
        if (selectedButtons.includes(item.id)) {
            const removeItem = selectedButtons.filter((selectedButtonId: string) => selectedButtonId !== item.id);
            setSelectedButtons([...removeItem]);
        } else {
            setSelectedButtons([...selectedButtons, item.id]);
        }
    };

    const handleCancelRemoveProductTypes = () => {
        setPendingProductObjectFilters(null);
        setPendingRemovedProductTypes([]);
    };

    const buttons = useMemo(() => getActiveButtonsByType(getCollection<Button>("buttons"), buttonType), [buttonType]);

    if (isFetchingPartnerExperiences || isFetchingPartnerPackages || isFetchingMerchandises || isFetchingCollections) {
        return <Loader />;
    }

    return (
        <XolaFormik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, values, errors, setFieldValue, setValues }) => {
                const handleOnApply = () => {
                    const [products, displayedProducts] = filterInsertListingFromButton(
                        buttons,
                        selectedButtons,
                        initialValues,
                    );
                    setValues({ ...values, products, displayedProducts });
                };

                const autoApplyDisabled = hasPartnerProducts(values.displayedProducts, sellerId);

                const productIds = values.products.map((p) => p.id).join(",");
                const dragAndDropKey = `drag-and-drop-${selectedButtonType}-${productIds}`;

                // Products Displayed column with search applied (same logic as DragDropComponent)
                let filteredDisplayed = values.displayedProducts;
                if (displayedSearch?.trim()) {
                    const term = displayedSearch.trim().toLowerCase();
                    filteredDisplayed = filteredDisplayed.filter((p) => p.name.trim().toLowerCase().includes(term));
                }

                // Tag counts: only from Products Displayed (filteredDisplayed)
                const countMap = new Map<ProductObject, number>();
                for (const product of filteredDisplayed) {
                    const count = countMap.get(product.object) ?? 0;
                    countMap.set(product.object, count + 1);
                }
                const productObjectFiltersWithCounts = productObjectFilters
                    .filter((filter) => {
                        return (
                            (allowCollectionsInMixedButtons || filter.productObject !== ProductObject.COLLECTION) &&
                            filter.productObject !== ProductObject.GIFT_OFFER
                        );
                    })
                    .map((filter) => ({
                        ...filter,
                        count: countMap.get(filter.productObject) ?? 0,
                    }));

                const enabledProductObjects = productObjectFilters.filter((f) => f.enabled).map((f) => f.productObject);

                const handleConfirmRemoveProductTypes = () => {
                    if (!pendingProductObjectFilters || pendingRemovedProductTypes.length === 0) {
                        setProductObjectFilters(pendingProductObjectFilters ?? productObjectFilters);
                        setPendingProductObjectFilters(null);
                        setPendingRemovedProductTypes([]);
                        return;
                    }

                    const removedObjects = new Set(pendingRemovedProductTypes.map((entry) => entry.productObject));
                    const removedDisplayedProducts = values.displayedProducts.filter((product) => {
                        return removedObjects.has(product.object);
                    });
                    const remainingDisplayedProducts = values.displayedProducts.filter((product) => {
                        return !removedObjects.has(product.object);
                    });
                    const nextProducts = [...values.products, ...removedDisplayedProducts];
                    const uniqueProducts = uniqBy(nextProducts, (product) => product.id);

                    setValues({
                        ...values,
                        products: uniqueProducts,
                        displayedProducts: remainingDisplayedProducts,
                    });
                    setProductObjectFilters(pendingProductObjectFilters);
                    setPendingProductObjectFilters(null);
                    setPendingRemovedProductTypes([]);
                };

                const handleProductObjectFiltersChange = (newFilters: ProductObjectFilter[]) => {
                    const normalizedFilters = !allowCollectionsInMixedButtons
                        ? (() => {
                              const previousCollectionEnabled = productObjectFilters.some(
                                  (filter) => filter.productObject === ProductObject.COLLECTION && filter.enabled,
                              );
                              const nextCollectionEnabled = newFilters.some(
                                  (filter) => filter.productObject === ProductObject.COLLECTION && filter.enabled,
                              );
                              const nextHasOtherEnabled = newFilters.some(
                                  (filter) => filter.productObject !== ProductObject.COLLECTION && filter.enabled,
                              );

                              if (!(nextCollectionEnabled && nextHasOtherEnabled)) {
                                  return newFilters;
                              }

                              const userSelectedCollection = !previousCollectionEnabled && nextCollectionEnabled;

                              return newFilters.map((filter) => {
                                  if (userSelectedCollection) {
                                      return filter.productObject === ProductObject.COLLECTION
                                          ? filter
                                          : { ...filter, enabled: false };
                                  }

                                  return filter.productObject === ProductObject.COLLECTION
                                      ? { ...filter, enabled: false }
                                      : filter;
                              });
                          })()
                        : newFilters;

                    const currentEnabled = new Set(
                        productObjectFilters.filter((filter) => filter.enabled).map((filter) => filter.productObject),
                    );
                    const nextEnabled = new Set(
                        normalizedFilters.filter((filter) => filter.enabled).map((filter) => filter.productObject),
                    );
                    const removedProductObjects = [...currentEnabled].filter((productObject) => {
                        return !nextEnabled.has(productObject);
                    });

                    if (removedProductObjects.length === 0) {
                        setProductObjectFilters(normalizedFilters);
                        return;
                    }

                    const removedCountsMap = new Map<ProductObject, number>();
                    for (const product of values.displayedProducts) {
                        if (!removedProductObjects.includes(product.object)) {
                            continue;
                        }
                        const count = removedCountsMap.get(product.object) ?? 0;
                        removedCountsMap.set(product.object, count + 1);
                    }

                    const removedProductTypes = removedProductObjects
                        .map((productObject) => ({
                            productObject,
                            count: removedCountsMap.get(productObject) ?? 0,
                        }))
                        .filter((entry) => entry.count > 0);

                    if (removedProductTypes.length === 0) {
                        setProductObjectFilters(normalizedFilters);
                        return;
                    }

                    setPendingProductObjectFilters(normalizedFilters);
                    setPendingRemovedProductTypes(removedProductTypes);
                };

                const isProductObjectDisabled = (productObject: ProductObject) => {
                    if (allowCollectionsInMixedButtons) {
                        return false;
                    }

                    const collectionEnabled = productObjectFilters.some(
                        (filter) => filter.productObject === ProductObject.COLLECTION && filter.enabled,
                    );
                    const nonCollectionEnabled = productObjectFilters.some(
                        (filter) => filter.productObject !== ProductObject.COLLECTION && filter.enabled,
                    );

                    if (collectionEnabled && productObject !== ProductObject.COLLECTION) {
                        return true;
                    }

                    if (nonCollectionEnabled && productObject === ProductObject.COLLECTION) {
                        return true;
                    }

                    return false;
                };

                return (
                    <fieldset>
                        <RemoveProductTypesModal
                            isOpen={pendingProductObjectFilters !== null}
                            removedProductTypes={pendingRemovedProductTypes}
                            onCancel={handleCancelRemoveProductTypes}
                            onConfirm={handleConfirmRemoveProductTypes}
                        />
                        <Modal.Header>
                            {selectedEditButton ? "Edit Checkout Button" : "Add Checkout Button"}
                        </Modal.Header>
                        <Modal.Body className="mt-8 flex flex-col space-y-4">
                            <FormField name="buttonName" label="Button Name" className="mb-0">
                                <InputField />
                            </FormField>

                            {hasX2CheckoutWithFullProbability() && (
                                <FormField name="type" label="Checkout Type">
                                    <div className="flex space-x-4">
                                        <RadioInput
                                            checked={selectedButtonType === ButtonType.CHECKOUT}
                                            onChange={() => setSelectedButtonType(ButtonType.CHECKOUT)}
                                        >
                                            Grid
                                        </RadioInput>
                                        <RadioInput
                                            checked={selectedButtonType === ButtonType.GIFT}
                                            onChange={() => setSelectedButtonType(ButtonType.GIFT)}
                                        >
                                            {/* We need this so that when this is selected we can show experience as gifts. We do not have any designs for showing experience as gifts in a normal grid view above */}
                                            Gifts
                                        </RadioInput>
                                        <RadioInput
                                            checked={selectedButtonType === ButtonType.TIMELINE}
                                            onChange={() => setSelectedButtonType(ButtonType.TIMELINE)}
                                        >
                                            Timeline
                                        </RadioInput>
                                        <RadioInput
                                            checked={selectedButtonType === ButtonType.DAY}
                                            onChange={() => setSelectedButtonType(ButtonType.DAY)}
                                        >
                                            Day
                                        </RadioInput>
                                    </div>
                                </FormField>
                            )}

                            {!isGiftButton && (
                                <div>
                                    <Switch.Group className="flex items-center">
                                        <Tooltip
                                            disabled={!autoApplyDisabled}
                                            content="Auto apply not allowed with partner experience/package products."
                                        >
                                            <div className="flex items-center space-x-2">
                                                <Switch
                                                    size="medium"
                                                    name="isAutoApplyCode"
                                                    isChecked={values.isAutoApplyCode && !autoApplyDisabled}
                                                    disabled={autoApplyDisabled}
                                                    onChange={(value: boolean) => {
                                                        setFieldValue("isAutoApplyCode", value);
                                                        if (!value) {
                                                            setFieldValue("autoApplyCode", null);
                                                        }
                                                    }}
                                                />
                                                <div className="flex items-center space-x-2">
                                                    <Switch.Label className="text-gray-darker" direction="left">
                                                        Automatically apply code
                                                    </Switch.Label>
                                                </div>
                                            </div>
                                        </Tooltip>

                                        {values.isAutoApplyCode && !autoApplyDisabled && (
                                            <div className="relative -top-2">
                                                <Label className={clsx(errors?.autoApplyCode && "!text-danger")}>
                                                    {errors.autoApplyCode}
                                                </Label>
                                                <FormField name="autoApplyCode" className="mb-0">
                                                    <InputField isRequired />
                                                </FormField>
                                            </div>
                                        )}
                                        <Tooltip content="This code will automatically get applied during online checkout for all purchases originating from this button">
                                            <CircleInfoIcon />
                                        </Tooltip>
                                    </Switch.Group>
                                </div>
                            )}

                            <div>
                                <div className="mb-3 flex flex-col flex-wrap gap-2">
                                    <ProductObjectFilterDropdown
                                        productObjectFilters={productObjectFiltersWithCounts}
                                        isProductObjectDisabled={isProductObjectDisabled}
                                        onProductObjectFiltersChange={handleProductObjectFiltersChange}
                                    />
                                </div>
                                <div className="flex justify-between space-x-4">
                                    <SearchProducts
                                        key={`notDisplayed-${searchKey}`}
                                        label="Products Not Displayed"
                                        value={notDisplayedSearch}
                                        onChange={setNotDisplayedSearch}
                                    />
                                    <SearchProducts
                                        key={`displayed-${searchKey}`}
                                        label="Products Displayed"
                                        value={displayedSearch}
                                        onChange={setDisplayedSearch}
                                    />
                                </div>
                                <div className="flex h-80 justify-between space-x-4">
                                    <DragDropComponent
                                        key={dragAndDropKey}
                                        data={values.products}
                                        displayedProducts={values.displayedProducts}
                                        notDisplayedSearchValue={notDisplayedSearch}
                                        displayedSearchValue={displayedSearch}
                                        enabledProductObjects={
                                            productObjectFilters.length > 0 ? enabledProductObjects : undefined
                                        }
                                        onMove={(data: Product[][]) => {
                                            const [products, displayedProducts] = data;
                                            setValues({ ...values, products, displayedProducts });
                                        }}
                                    />
                                </div>
                            </div>
                        </Modal.Body>
                        <Modal.Footer className="flex justify-between">
                            <div className="flex space-x-2">
                                <MultiSelectItemFromAnotherButton
                                    buttons={buttons}
                                    selectedButtons={selectedButtons}
                                    onApply={handleOnApply}
                                    onChange={handleOnCheckButton}
                                />
                                <SecondaryButton
                                    onClick={() => {
                                        setSelectedButtons([]);
                                        setValues(initialValues);
                                        setNotDisplayedSearch("");
                                        setDisplayedSearch("");
                                        setSearchKey((k) => k + 1);
                                    }}
                                >
                                    Clear All
                                </SecondaryButton>
                            </div>
                            <div className="flex space-x-2">
                                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                                <SubmitButton isLoading={isLoading || isLoadingUpdating} onClick={handleSubmit}>
                                    Save
                                </SubmitButton>
                            </div>
                        </Modal.Footer>
                    </fieldset>
                );
            }}
        </XolaFormik>
    );
};

interface SearchProductsProps {
    onChange: (value: string) => void;
    value: string;
    label: string;
}

const SearchProducts = ({ value, onChange, label }: SearchProductsProps) => (
    <div className="flex-1">
        <Label>{label}</Label>
        <SearchInput value={value} onChange={onChange} />
    </div>
);

interface MultiSelectItemFromAnotherButtonProps {
    buttons: Button[];
    selectedButtons: string[];
    onApply: () => void;
    onChange: (button: Button) => void;
}

const MultiSelectItemFromAnotherButton = ({
    buttons,
    selectedButtons,
    onApply,
    onChange,
}: MultiSelectItemFromAnotherButtonProps) => {
    const insertFromAnotherButtonModal = useModal(false);
    return (
        <MultiSelectPopover
            zIndex={30}
            placement="top"
            title="Buttons"
            isVisible={insertFromAnotherButtonModal.isVisible}
            selectedCount={selectedButtons.length}
            target={
                <SecondaryButton onClick={insertFromAnotherButtonModal.show}>
                    Insert product from another button
                </SecondaryButton>
            }
            showSearch={false}
            onApply={() => {
                onApply();
                insertFromAnotherButtonModal.hide();
            }}
            onCancel={insertFromAnotherButtonModal.hide}
        >
            {buttons.map((button) => (
                <MultiSelectPopover.Item key={button.id}>
                    <Checkbox
                        label={button.name}
                        checked={selectedButtons.includes(button.id)}
                        data-testid="event-change-arrival-blackout-old-timeslot-checkbox"
                        onChange={() => onChange(button)}
                    />
                </MultiSelectPopover.Item>
            ))}
        </MultiSelectPopover>
    );
};
